import { useEffect, useRef, useState } from 'react';
import Parser from 'rss-parser';
import InputForm, { InputFormRef } from '../components/InputForm';
import { MortgageData } from "../components/InputForm";
import ChartModeller from '../components/ChartModeller';
import TableModeller from '../components/TableModeller';
import SummaryBox from '../components/SummaryBox';
import IntroBox from '../components/IntroBox';
import Comparisons from '../components/Comparisons';
import { MortgageProps, initialMortgageState } from '../contexts/MortgageContextProvider';

const MortgageModeller = () => {
  const inputFormRef = useRef<InputFormRef>(null);
  const inputFormRef2 = useRef<InputFormRef>(null);
  const defaultData: MortgageData = { // default values
    mortgageType: "repayment",
    interestFrequency: "monthly",
    interestValue: '0',
    mortgageAmountValue: '0',
    mortgageTermValue: '0',
    repaymentFrequency: "monthly",
    showOverpayments: false,
    overpaymentsValue: '0',
    depositValue: '0',
    showOffset: false,
    offsetValue: '0',
    showEquity: false,
    housePriceInterestValue: '0',
    showAdvanced: false,
    offsetRegular: '0',
    addBOE: false,
    productFee: '0',
    useNominal: false,
    date: ''
  }

  // States for UI
  const [labels, setLabels] = useState<string[]>([]);
  const [mortgageState, setMortgageState] = useState<MortgageProps>(initialMortgageState);
  const [offsetState, setOffsetState] = useState<MortgageProps>(initialMortgageState);
  const [overpaymentState, setOverpaymentState] = useState<MortgageProps>(initialMortgageState);
  const [secondMortgageState, setSecondMortgageState] = useState<MortgageProps>(initialMortgageState);
  const [allDataSets, setAllDataSets] = useState<MortgageProps[]>([]);
  const [equityDataSets, setEquityDataSets] = useState<MortgageProps[]>([]);
  const [mainMortgageData, setMainMortgageData] = useState<MortgageProps[]>([]);
  const [secondMortgageData, setSecondMortgageData] = useState<MortgageProps[]>([]);
  let mainMortgageInput: MortgageData = defaultData;
  let secondMortgageInput: MortgageData = defaultData;
  const [repaymentFrequency, setRepaymentFrequency] = useState<string>('Monthly');
  const [boeInterestRateState, setBoeInterestRateState] = useState<string>("5");
  const [interestRateState, setInterestRateState] = useState<string>("0");
  const [interestRateStateTwo, setInterestRateStateTwo] = useState<string>("0");
  const [compareTwo, setCompareTwo] = useState<boolean>(false);
  const [showEquity, setShowEquity] = useState<boolean>(false);
  const [showOffset, setShowOffset] = useState(false);
  const [showOverpayment, setShowOverpayment] = useState(false);
  const [startDate, setStartDate] = useState<string>('');

  // Variables for calculations
  var boeInterestRate = useRef<number>(5); // Needed useRef so it doesn't reset to 0 on re-render

  useEffect(() => { // Only called once on load
    // fetchBoeInterestRate();
    handleResetClick();
  }, [])

  interface RSSFeed {
    items: Array<{
      title?: string;
      link?: string;
    }>;
  }

  const fetchBoeInterestRate = async () => {
    const parser = new Parser();
    const feedUrl = 'https://mortgage-modoller-cors-anywhere.onrender.com/https://www.bankofengland.co.uk/rss/news';
    try {
      const feed: RSSFeed = await new Promise((resolve, reject) => {
        parser.parseURL(feedUrl, (err, feed) => {
          if (err) reject(err);
          else resolve(feed as RSSFeed); // Cast the feed to RSSFeed type
        });
      });
      const searchTerm = 'Bank rate';
      const targetItem = feed.items.find((item) => item.title && item.title.includes(searchTerm));
      if (targetItem) {
        const rateText = targetItem.title || '';
        const rateValue = parseFloat(rateText.match(/(\d+\.\d+)/)?.[0] || '0');
        setBoeInterestRateState(String(rateValue));
        boeInterestRate.current = rateValue;
      } else {
        setBoeInterestRateState("error please refresh");
      }
    } catch (error) {
      console.error("Error fetching BOE interest rate:", error);
      setBoeInterestRateState("error please refresh");
    }
  };

  function createYearLabels(mortgageTermInYears: number): string[] {
    var labels: string[] = [];
    for (let i = 0; i <= mortgageTermInYears; i++) { // X axis
      let label = i.toString();
      labels.push(label);
    }
    return labels;
  }

  const calculateNominalInterestRate = (
    effectiveRatePerPeriod: number,
    originalCompoundingPeriodsPerYear: number,
    newCompoundingPeriodsPerYear: number
  ): number => {
    const effectiveRateDecimal = effectiveRatePerPeriod / 100;
  
    // Calculate the nominal interest rate adjusted for the new compounding frequency
    const nominalInterestRate = newCompoundingPeriodsPerYear *
      (Math.pow(1 + effectiveRateDecimal / originalCompoundingPeriodsPerYear, originalCompoundingPeriodsPerYear / newCompoundingPeriodsPerYear) - 1);
  
    return nominalInterestRate;
  };

  function pmt(ir: number, np: number, pv: number, fv: number, type: number): number {
    /*
     * ir   - interest rate per month
     * np   - number of periods (months)
     * pv   - present value
     * fv   - future value
     * type - when the payments are due:
     *        0: end of the period, e.g. end of month (default)
     *        1: beginning of period
     */
    var pmt, pvif;
    fv || (fv = 0);
    type || (type = 0);
    if (ir === 0) return (-(pv + fv) / np) * -1;
    pvif = Math.pow(1 + ir, np);
    pmt = - ir * (pv * pvif + fv) / (pvif - 1);
    if (type === 1) pmt /= (1 + ir);
    return pmt * -1;
  }

  function calculateInterestOnlyPayment(mortgageAmount: number, interestRate: number): number {
    const interestAdded = mortgageAmount * interestRate;
    return interestAdded;
  }

  function calculateStatsPerYear(name: string, colour: string, monthlyPayment: number, mortgageAmount: number,
    interestRate: number, mortgageTermInYears: number, repaymentFrequency: string, offset: number, 
    regularOffsetSave: number, interestFrequency: string, productFee: number, date: string): MortgageProps {
    const newState: MortgageProps = {
      name: name,
      colour: colour,
      totalInterest: 0,
      totalAmountPaid: productFee,
      yearlyData: [mortgageAmount],
      monthlyData: [mortgageAmount],
      monthlyPayment: monthlyPayment,
      totalInterestPaidPerYear: [0],
      totalInterestPaidPerMonth: [0],
      totalAmountPaidPerYear: [0],
      totalAmountPaidPerMonth: [0],
      interestAddedYearly: [0],
      interestAddedMonthly: [0],
      amountPaidYearly: [productFee],
      amountPaidMonthly: [productFee],
    };
    let debt = mortgageAmount;
    let totalAmountPaid = productFee;
    let totalInterestPaid = 0;
    let currOffset = offset;
    let totalDaysPassed = 0;
    let currentDate: Date = new Date();
    if (date !== '') {
      currentDate = new Date(date);
    }
    
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-based

    for (let year = 0; year <= mortgageTermInYears; year++) {
      const fullYear = currentYear + year;
      const isLeapYear = (fullYear % 4 === 0 && fullYear % 100 !== 0) || (fullYear % 400 === 0);
      let monthDays = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      const startMonth = year === 0 ? currentMonth : 1; // Start from current month in the first year, January otherwise
      const endMonth = year < mortgageTermInYears ? 12 : currentMonth; // In the last iteration, loop until the month before the start month

      let interestPaidThisYear = 0;
      let amountPaidThisYear = 0;
      for (let month = startMonth; month <= endMonth; month++) {
        let monthlyInterest = 0;
        let amountPaidThisMonth = 0;
        currOffset = currOffset + regularOffsetSave;
        let debtOffset = debt - currOffset;
        if (debtOffset < 0) {
          debtOffset = 0;
        }
        for (let day = 1; day <= monthDays[month - 1]; day++) { // Go through each day
          let amountPaidThisPeriod = 0;
          totalDaysPassed++;

          // Calculate interest
          if (interestFrequency === "daily") {
            monthlyInterest += debtOffset * interestRate / (isLeapYear ? 366 : 365); // Daily interest calculation
          } else if (interestFrequency === "monthly" && day === monthDays[month - 1]) {
            monthlyInterest += debtOffset * interestRate / 12;
          }

          // Calculate payment
          if (repaymentFrequency === "monthly" && day === monthDays[month - 1]) {
            amountPaidThisPeriod = monthlyPayment;
          } else if ((repaymentFrequency === "fortnightly" || repaymentFrequency === "accelerated biweekly") && totalDaysPassed % 14 === 0) {
            amountPaidThisPeriod = monthlyPayment;
          } else if ((repaymentFrequency === "weekly"  || repaymentFrequency === "accelerated weekly") && totalDaysPassed % 7 === 0) {
            amountPaidThisPeriod = monthlyPayment;
          }

          debt -= amountPaidThisPeriod;
          amountPaidThisMonth += amountPaidThisPeriod;
        }

        debt += monthlyInterest;
        totalAmountPaid += amountPaidThisMonth;
        amountPaidThisYear += amountPaidThisMonth;
        interestPaidThisYear += monthlyInterest;
        totalInterestPaid += monthlyInterest;
        
        if (debt < 0.01) { // monthly stats added
          newState.monthlyData.push(0);
          totalAmountPaid += debt; // Overshoot values
          amountPaidThisMonth += debt;
          newState.amountPaidMonthly.push(+amountPaidThisMonth.toFixed(2));
          newState.totalAmountPaidPerMonth.push(+totalAmountPaid.toFixed(2));
          newState.totalInterestPaidPerMonth.push(+totalInterestPaid.toFixed(2));
          newState.interestAddedMonthly.push(+monthlyInterest.toFixed(2));
          break;
        } else {
          newState.monthlyData.push(+debt.toFixed(2));
          newState.amountPaidMonthly.push(+amountPaidThisMonth.toFixed(2));
          newState.totalAmountPaidPerMonth.push(+totalAmountPaid.toFixed(2));
          newState.totalInterestPaidPerMonth.push(+totalInterestPaid.toFixed(2));
          newState.interestAddedMonthly.push(+monthlyInterest.toFixed(2));
        }
      }
    
      if (debt < 0.01) { // Yearly stats added
        newState.yearlyData.push(0);
        amountPaidThisYear += debt;
        newState.totalAmountPaidPerYear.push(+totalAmountPaid.toFixed(2));
        newState.amountPaidYearly.push(+amountPaidThisYear.toFixed(2));
        newState.totalInterestPaidPerYear.push(+totalInterestPaid.toFixed(2));
        newState.interestAddedYearly.push(+interestPaidThisYear.toFixed(2));
        break;
      } else {
        newState.yearlyData.push(+debt.toFixed(2));
        newState.totalAmountPaidPerYear.push(+totalAmountPaid.toFixed(2));
        newState.amountPaidYearly.push(+amountPaidThisYear.toFixed(2));
        newState.totalInterestPaidPerYear.push(+totalInterestPaid.toFixed(2)); 
        newState.interestAddedYearly.push(+interestPaidThisYear.toFixed(2));
      }
    }
    newState.totalInterest = +totalInterestPaid.toFixed(2);
    newState.totalAmountPaid = +totalAmountPaid.toFixed(2);
    console.log(newState)
    return newState;
  }

  function calculateHousePriceData(label: string, housePrice: number, housePriceInterestRate: number, mortgageTermInYears: number, colour: string): MortgageProps {
    const newState: MortgageProps = {
      name: label,
      colour: colour,
      totalInterest: 0,
      totalAmountPaid: 0,
      yearlyData: [housePrice],
      monthlyData: [housePrice],
      monthlyPayment: 0,
      totalInterestPaidPerYear: [0],
      totalInterestPaidPerMonth: [0],
      totalAmountPaidPerYear: [0],
      totalAmountPaidPerMonth: [0],
      interestAddedYearly: [0],
      interestAddedMonthly: [0],
      amountPaidYearly: [0],
      amountPaidMonthly: [0],
    };
    let monthlyHousePrice = housePrice;
    for (let year = 0; year <= mortgageTermInYears; year++) {
      for (let month = 1; month <= 12; month++) {
        monthlyHousePrice = monthlyHousePrice * (1 + housePriceInterestRate / 1200);
        newState.monthlyData.push(Math.round(monthlyHousePrice))
      }
      newState.yearlyData.push(Math.round(monthlyHousePrice));
    }
    newState.yearlyData.push(Math.round(monthlyHousePrice * (1 + housePriceInterestRate / 100)));
    return newState;
  }

  function calculateEquityAndLTV(housePriceData: MortgageProps, mortgageData: MortgageProps): MortgageProps {
    const equityAndLTVData: MortgageProps = {
      name: 'Equity',
      colour: 'rgba(50,205,50,1)', // Green color for equity
      totalInterest: 0,
      totalAmountPaid: 0,
      yearlyData: [],
      monthlyData: [],
      monthlyPayment: 0,
      totalInterestPaidPerYear: [], // Repurposing to store yearly LTV values
      totalInterestPaidPerMonth: [], // Repurposing to store monthly LTV values
      totalAmountPaidPerYear: [],
      totalAmountPaidPerMonth: [],
      interestAddedYearly: [],
      interestAddedMonthly: [],
      amountPaidYearly: [],
      amountPaidMonthly: [],
    };

    // Calculate yearly equity and LTV
    for (let i = 0; i < housePriceData.yearlyData.length; i++) {
      const equity = housePriceData.yearlyData[i] - mortgageData.yearlyData[i];
      equityAndLTVData.yearlyData.push(equity);
      // Calculate LTV as a percentage
      const ltv = (mortgageData.yearlyData[i] / housePriceData.yearlyData[i]) * 100;
      equityAndLTVData.totalInterestPaidPerYear.push(ltv);
    }

    for (let i = 0; i < housePriceData.monthlyData.length; i++) {
      const equity = housePriceData.monthlyData[i] - mortgageData.monthlyData[i];
      equityAndLTVData.monthlyData.push(equity);
      const ltv = (mortgageData.monthlyData[i] / housePriceData.monthlyData[i]) * 100;
      equityAndLTVData.totalInterestPaidPerMonth.push(ltv);
    }
    return equityAndLTVData;
  }

  function calculateOffsetData(label: string, offset: number, regularOffsetSave: number, mortgageTermInYears: number, colour: string): MortgageProps {
    const newState: MortgageProps = {
      name: label,
      colour: colour,
      totalInterest: 0,
      totalAmountPaid: 0,
      yearlyData: [offset],
      monthlyData: [offset],
      monthlyPayment: 0,
      totalInterestPaidPerYear: [0],
      totalInterestPaidPerMonth: [0],
      totalAmountPaidPerYear: [0],
      totalAmountPaidPerMonth: [0],
      interestAddedYearly: [0],
      interestAddedMonthly: [0],
      amountPaidYearly: [0],
      amountPaidMonthly: [0],
    };
    let currOffset = offset;
    for (let i = 0; i <= mortgageTermInYears; i++) {
      for (let j = 1; j <= 12; j++) {
        currOffset = currOffset + regularOffsetSave;
        newState.monthlyData.push(Math.round(currOffset));
      }
      newState.yearlyData.push(Math.round(currOffset));
    }
    return newState;
  }

  function calculateMonthlyPayment(mortgageAmount: number, interestRate: number, mortgageTermInYears: number, paymentFrequency: string, interestFrequency: string, useNominal: boolean): number {
    const interestPeriods: number = interestFrequency === "monthly" ? 12 : 365.25;
    let nominalInterestRate: number = interestRate;
    if (useNominal) {
      nominalInterestRate = calculateNominalInterestRate(interestRate * 100, interestPeriods, 12);
    }
    
    let monthlyPayment = pmt(nominalInterestRate / 12, mortgageTermInYears * 12, mortgageAmount, 0, 0);
    console.log(interestRate)
    switch (paymentFrequency) {
      case "accelerated biweekly":
        monthlyPayment = monthlyPayment / 2;
        break;
      case "accelerated weekly":
        monthlyPayment = monthlyPayment / 4;
        break;
      case "fortnightly":
        if (useNominal) {
          nominalInterestRate = calculateNominalInterestRate(interestRate * 100, interestPeriods, 12);
        } 
        monthlyPayment = pmt(nominalInterestRate / 26, mortgageTermInYears * 26, mortgageAmount, 0, 0);
        break;
      case "weekly":
        if (useNominal) {
          nominalInterestRate = calculateNominalInterestRate(interestRate * 100, interestPeriods, 12);
        } 
        monthlyPayment = pmt(nominalInterestRate / 52, mortgageTermInYears * 52, mortgageAmount, 0, 0);
        break;
      default:
        break;
    }

    return Number(monthlyPayment);
  }

  function handleStats(whichForm: string) {
    const mortgageData: MortgageData = whichForm === "mainMortgage" ? mainMortgageInput : secondMortgageInput;
    var monthlyPayment: number = 0;
    var datasets: MortgageProps[] = [];
    const interestFrequency: string = mortgageData.interestFrequency;
    const interestRate: number = mortgageData.addBOE ? (Number(mortgageData.interestValue) + boeInterestRate.current) / 100 : Number(mortgageData.interestValue) / 100;
    var mortgageAmount: number = Number(mortgageData.mortgageAmountValue);
    const mortgageTermInYears: number = Number(mortgageData.mortgageTermValue);
    const paymentFrequency: string = mortgageData.repaymentFrequency;
    const housePrice: number = Number(mortgageData.depositValue) + mortgageAmount;
    const housePriceInterest: number = Number(mortgageData.housePriceInterestValue);
    const paymentsPerYear: number = paymentFrequency === 'monthly' ? 12 : (paymentFrequency === 'fortnightly' ? 26 : 52);
    const productFee: number = Number(mortgageData.productFee);
    var offsetValue: number = 0;
    var regularOffsetSave: number = 0;
    const date: string = mortgageData.date;

    // Set the label and colours for the chart
    const label: string = whichForm === "mainMortgage" ? "Mortgage" : "2nd Mortgage";
    let mainLabel: string = label;
    const principleColour: string = whichForm === "mainMortgage" ? "rgba(0,0,255, 1)" : "rgba(173, 216, 230)";
    const offsetColour: string = whichForm === "mainMortgage" ? "rgba(160, 32, 240)" : "rgba(203, 195, 227)";
    const offsetSavingsColour: string = whichForm === "mainMortgage" ? "rgba(252, 132, 3, 1)" : "rgba(255, 213, 128)";
    const housePriceColour: string = whichForm === "mainMortgage" ? "rgb(178,34,34, 1)" : "rgba(255, 127, 127)";
    const overpaymentColour: string = whichForm === "mainMortgage" ? "rgba(0,128,0, 1)" : "rgba(175, 225, 175)";

    let showOffset: boolean = mortgageData.showOffset
    let showOverpayment: boolean = mortgageData.showOverpayments

    if (mortgageData.mortgageType === "repayment" || mortgageData.mortgageType === "offset") {
      monthlyPayment = calculateMonthlyPayment(mortgageAmount, interestRate, mortgageTermInYears, paymentFrequency, interestFrequency, mortgageData.useNominal);
    } else if (mortgageData.mortgageType === "interestOnly") {
      monthlyPayment = calculateInterestOnlyPayment(mortgageAmount, interestRate / paymentsPerYear);
      showOffset = false
      showOverpayment = false
    }

    if (showOffset) { // offset
      offsetValue = Number(mortgageData.offsetValue);
      regularOffsetSave = Number(mortgageData.offsetRegular)
      const offsetData: MortgageProps = calculateStatsPerYear("Without Offset", offsetColour, monthlyPayment, mortgageAmount, interestRate, mortgageTermInYears, paymentFrequency, 0, 0, interestFrequency, productFee, date);
      const offsetSavingsData: MortgageProps = calculateOffsetData("Offset Savings", offsetValue, regularOffsetSave, mortgageTermInYears, offsetSavingsColour);
      datasets.push(offsetSavingsData); // each dataset pushed is another line on the chart
      datasets.push(offsetData);
      setOffsetState(offsetData);
      mainLabel = label + " with Offset";
    }

    if (showOverpayment) { // overpayments
      let overpaymentM = monthlyPayment;
      monthlyPayment += Number(mortgageData.overpaymentsValue);
      const overPaymentData = calculateStatsPerYear("Without Overpayment", overpaymentColour, overpaymentM, mortgageAmount, interestRate, mortgageTermInYears, paymentFrequency, offsetValue, regularOffsetSave, interestFrequency, productFee, date);
      datasets.push(overPaymentData);
      setOverpaymentState(overPaymentData)
      mainLabel = label + " with Overpayment";
    }

    if (showOffset && showOverpayment) { // offset and overpayments
      mainLabel = label + " with Offset and Overpayment";
    }

    // This is for the mortgage
    // console.log(interestRate)
    const data: MortgageProps = calculateStatsPerYear(mainLabel, principleColour, monthlyPayment, mortgageAmount, interestRate, mortgageTermInYears, paymentFrequency, offsetValue, regularOffsetSave, interestFrequency, productFee, date)
    datasets.push(data);

    if (mortgageData.showEquity) { 
      var equityDatasets: MortgageProps[] = [];
      const housePriceData = calculateHousePriceData("House Price", housePrice, housePriceInterest, mortgageTermInYears, housePriceColour);
      const equityData = calculateEquityAndLTV(housePriceData, data);
      equityDatasets.push(housePriceData);
      equityDatasets.push(equityData);
      setEquityDataSets([...equityDatasets]);
    }


    if (whichForm === "mainMortgage") {
      setMortgageState(data);
      setMainMortgageData(datasets);
      setInterestRateState(String(Math.round(interestRate * 100 * 100) / 100));
    } else if (whichForm === "secondMortgage") {
      setSecondMortgageState(data);
      setSecondMortgageData(datasets);
      setInterestRateStateTwo(String(Math.round(interestRate * 100 * 100) / 100));
    }
    setLabels(createYearLabels(mortgageTermInYears)); // Needed for table update
    
    setRepaymentFrequency(repaymentFrequency.charAt(0).toUpperCase() + repaymentFrequency.slice(1));
    setShowOffset(mortgageData.showOffset);
    setShowOverpayment(mortgageData.showOverpayments);
    setShowEquity(mortgageData.showEquity);
    setStartDate(date);
  }

  useEffect(() => {
    // This useEffect will trigger a re-calculation of mortgageData anytime
    if (compareTwo) {
      setAllDataSets([mortgageState, secondMortgageState]);
    } else {
      setAllDataSets(mainMortgageData);
    }
  }, [mainMortgageData,mortgageState, secondMortgageData, compareTwo, showEquity]);

  function handleDataUpdateMainMortgage(data: MortgageData) {
      mainMortgageInput = data;
      console.log(data)
      handleStats("mainMortgage");
  };

  function handleDataUpdateMortgageTwo(data: MortgageData) {
      secondMortgageInput = data;
      handleStats("secondMortgage");
  };

  const handleResetClick = () => {
    inputFormRef.current?.resetForm();
    inputFormRef2.current?.resetForm();
    
    setMortgageState(initialMortgageState)
    setSecondMortgageState(initialMortgageState)
    setInterestRateState("0")
    setInterestRateStateTwo("0")
    setShowOffset(false)
    setShowOverpayment(false)
    setShowEquity(false)
    setOffsetState(initialMortgageState)
    setOverpaymentState(initialMortgageState)
    setAllDataSets([]);
    setMainMortgageData([])
    console.log(allDataSets)
    // setLabels([])
  };

  const handleCompareTwo = () => {
    setCompareTwo(!compareTwo)
    console.log(allDataSets)
  }

  return (
    <>
      <div className="hero">
        <div className="w-full flex-col lg:flex-row">
          <div className="pb-4 px-20">
            <IntroBox onResetClick={handleResetClick} onCompareClick={handleCompareTwo} boeInterestRate={boeInterestRateState} />
          </div>
        </div>
      </div>
      <div className={`flex w-full pb-4 px-20 ${compareTwo ? 'flex-col px-40' : 'flex-col lg:flex-row'}`}>
        <div className={` ${compareTwo ? 'flex gap-6' : 'flex-grow'}`}>
          <InputForm ref={inputFormRef} updateData={handleDataUpdateMainMortgage} isCompareVisible={compareTwo}  whichMortgage={"Mortgage 1"}/>
          {compareTwo && (<>
            <InputForm ref={inputFormRef2} updateData={handleDataUpdateMortgageTwo} isCompareVisible={compareTwo} whichMortgage={"Mortgage 2"}/>
          </>
          )}
        </div>
        <div className={` ${compareTwo ? 'divider' : 'divider lg:divider-horizontal'}`}></div>
        <div className={`${compareTwo ? 'place-content-start px-40' : ' place-content-start w-3/4'} space-y-4`}>
          <SummaryBox mortgageState={mortgageState}
            secondMortgageState={secondMortgageState}
            compareTwo={compareTwo}
            effectiveInterestRate={interestRateState} 
            effectiveInterestRateTwo={interestRateStateTwo} />
          <ChartModeller labels={labels} datasets={allDataSets} startDate={startDate}/>
          <Comparisons showOffset={showOffset}
            showOverpayment={showOverpayment}
            showTwoMortgages={compareTwo}
            mortgageData={mortgageState}
            secondMortgageData={secondMortgageState}
            offsetData={offsetState}
            overpaymentData={overpaymentState} />
            {(!compareTwo && showEquity) && (
              <>
              <h1 className="text-3xl font-bold pl-4">Equity and LTV</h1>
            <ChartModeller labels={labels} datasets={equityDataSets} startDate={startDate}/>
            </>)}
        </div>
      </div>
      <div className="hero">
        <div className="w-full flex-col lg:flex-row">
          <div className="px-20 py-8">
            <TableModeller labels={labels} remainingDebtPerYear={mortgageState.yearlyData}
              totalAmountPaidPerYear={mortgageState.totalAmountPaidPerYear}
              amountPaidYearly={mortgageState.amountPaidYearly}
              interestAddedPerYear={mortgageState.interestAddedYearly}
              interestPaidPerYear={mortgageState.totalInterestPaidPerYear}
              remainingDebtPerMonth={mortgageState.monthlyData}
              totalAmountPaidPerMonth={mortgageState.totalAmountPaidPerMonth}
              amountPaidMonthly={mortgageState.amountPaidMonthly}
              interestAddedPerMonth={mortgageState.interestAddedMonthly}
              interestPaidPerMonth={mortgageState.totalInterestPaidPerMonth} />
          </div>
        </div>
      </div>
    </>
  );
}

export default MortgageModeller;